import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import telegramService from '../services/telegram';

/**
 * Ссылка внутри markdown-текста.
 *
 * Правило одно на всё приложение: ссылка на наш же сайт открывается
 * внутри приложения роутером, любая внешняя — через Telegram.
 *
 * До этого поведение было разным в трёх местах: `GiveawayPage` умел
 * внутренние ссылки, но узнавал их только по текущему origin, а
 * `PlacePage` и `AdCard` не обрабатывали клик вовсе — ссылка уходила
 * в браузер и вырывала человека из мини-приложения.
 */

// Домены приложения. Ссылку на себя редакция пишет абсолютной
// («https://appnefspb.ru/place/...»), и она обязана открываться
// внутри — даже когда сама страница открыта с другого адреса
// (локалка, превью-домен), поэтому одного window.location.origin мало
const APP_HOSTS = ['appnefspb.ru', 'nefspb.ru'];

/**
 * Внутренний путь для ссылки — или null, если ссылка внешняя.
 * Экспортируется: тем же правилом пользуются обычные ссылки вне markdown.
 */
export const getInternalPath = (href) => {
  if (!href || typeof href !== 'string') return null;

  // Относительная ссылка — всегда наша. Кроме «//host», это внешний адрес
  if (href.startsWith('/') && !href.startsWith('//')) return href;

  let url;
  try {
    url = new URL(href, window.location.origin);
  } catch {
    return null;
  }

  // Только http(s): tel:, mailto: и tg: обязаны уходить наружу
  if (url.protocol !== 'http:' && url.protocol !== 'https:') return null;

  const host = url.hostname.replace(/^www\./, '');
  const isOwnHost =
    host === window.location.hostname.replace(/^www\./, '') ||
    APP_HOSTS.includes(host);

  return isOwnHost ? `${url.pathname}${url.search}${url.hash}` : null;
};

// className по умолчанию — здесь: React 19 игнорирует defaultProps,
// и ссылки в описаниях оставались без стиля
const MarkdownLink = ({ href, children, className = 'text-action-text underline', ...props }) => {
  const navigate = useNavigate();
  const internalPath = getInternalPath(href);

  if (internalPath) {
    return (
      <a
        href={href}
        className={className}
        onClick={(e) => {
          e.preventDefault();
          navigate(internalPath);
        }}
        {...props}
      >
        {children}
      </a>
    );
  }

  return (
    <a
      href={href}
      className={className}
      target="_blank"
      rel="noopener noreferrer"
      onClick={(e) => {
        e.preventDefault();
        // try_instant_view выключен: предпросмотр Telegram ломает вёрстку
        // страниц, которые к нему не готовы
        telegramService.openLink(href, { try_instant_view: false });
      }}
      {...props}
    >
      {children}
    </a>
  );
};

MarkdownLink.propTypes = {
  href: PropTypes.string,
  children: PropTypes.node,
  className: PropTypes.string,
};

export default MarkdownLink;
